/**
 * AnalyticsService - Handles all analytics tracking with batching and opt-out support
 * Features:
 * - Batched events (30-second intervals)
 * - Opt-out mechanism via localStorage
 * - Privacy-conscious (hashed session IDs, no PII)
 * - FPS monitoring
 * - Firebase Analytics + Custom Firestore events
 */

import { logEvent, Analytics } from 'firebase/analytics';
import { collection, addDoc, Timestamp, Firestore } from 'firebase/firestore';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('Analytics');
import {
  AnalyticsEventType,
  AnalyticsEvent,
  QueuedEvent,
  DeviceInfo,
  AnalyticsEventData,
  SessionEventData,
  SequenceEventData,
  StageNavigationData,
  StageViewData,
  StageSelectorData,
  VRSessionData,
  VRControllerData,
  VideoEventData,
  FOVZoomData,
  ControllerButtonData,
  PerformanceEventData,
  ErrorEventData,
  LeadFormEventData,
} from './types';
import { DeviceDetector } from '../utils/DeviceDetector';

export class AnalyticsService {
  private analytics: Analytics | null = null;
  private firestore: Firestore | null = null;
  private enabled: boolean = true;
  private sessionId: string = '';
  private sequenceId: string | null = null;
  private userId: string | null = null;
  private deviceInfo: DeviceInfo = {
    type: 'unknown',
    browser: 'Unknown',
    os: 'Unknown',
    isVR: false
  };
  private eventQueue: QueuedEvent[] = [];
  private batchInterval: number = 30000; // 30 seconds
  private batchTimer: NodeJS.Timeout | null = null;
  private sessionStartTime: number = Date.now();
  private vrSessionStartTime: number | null = null;
  private currentStageStartTime: number | null = null;
  private currentStageIndex: number = 0;
  private fpsMonitorInterval: NodeJS.Timeout | null = null;
  private lastFrameTime: number = performance.now();
  private frameCount: number = 0;
  private isInVR: boolean = false;

  constructor(analytics: Analytics | null, firestore: Firestore | null) {
    this.analytics = analytics;
    this.firestore = firestore;

    // Check opt-out preference
    const optOut = localStorage.getItem('analytics_optout');
    if (optOut === 'true') {
      this.enabled = false;
      logger.debug(' Analytics disabled by user preference');
      return;
    }

    // Generate session ID (hashed for privacy)
    this.sessionId = this.generateSessionId();

    // Detect device info
    this.deviceInfo = this.detectDevice();

    // Start batch timer
    this.startBatchTimer();

    // Start FPS monitoring
    this.startFPSMonitoring();

    logger.debug(' Service initialized', {
      sessionId: this.sessionId,
      deviceType: this.deviceInfo.type,
    });
  }

  /**
   * Set sequence and user context
   */
  setContext(sequenceId: string | null, userId: string | null): void {
    this.sequenceId = sequenceId;
    this.userId = userId;
    logger.debug(' Context updated:', { sequenceId, userId });
  }

  /**
   * Enable or disable analytics tracking
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    logger.debug(' Analytics tracking', enabled ? 'enabled' : 'disabled');
  }

  /**
   * Generate or retrieve persistent user ID (hashed for privacy)
   * Uses localStorage to track returning users over 30 days
   */
  private generateSessionId(): string {
    const STORAGE_KEY = 'webxr_analytics_user_id';
    const EXPIRATION_KEY = 'webxr_analytics_expiry';
    const EXPIRATION_DAYS = 30;

    try {
      // Check for existing user ID in localStorage
      const storedUserId = localStorage.getItem(STORAGE_KEY);
      const storedExpiry = localStorage.getItem(EXPIRATION_KEY);

      if (storedUserId && storedExpiry) {
        const expiryTime = parseInt(storedExpiry, 10);
        const now = Date.now();

        // If not expired, return existing user ID
        if (now < expiryTime) {
          logger.debug(' Using existing user ID from localStorage');
          return storedUserId;
        } else {
          logger.debug(' User ID expired, generating new one');
        }
      }
    } catch (error) {
      logger.warn(' localStorage not available:', error);
      // Continue to generate new ID if localStorage fails
    }

    // Generate new user ID
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    const combined = `${timestamp}-${random}`;

    // Simple hash for privacy
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    const userId = `user_${Math.abs(hash).toString(36)}`;

    try {
      // Store in localStorage with expiration
      const expiryTime = Date.now() + (EXPIRATION_DAYS * 24 * 60 * 60 * 1000);
      localStorage.setItem(STORAGE_KEY, userId);
      localStorage.setItem(EXPIRATION_KEY, expiryTime.toString());
      logger.debug(' Generated new user ID and stored in localStorage');
    } catch (error) {
      logger.warn(' Could not store user ID in localStorage:', error);
    }

    return userId;
  }

  /**
   * Detect device information
   */
  private detectDevice(): DeviceInfo {
    const userAgent = navigator.userAgent;

    // Detect browser
    let browser = 'Unknown';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Edge')) browser = 'Edge';

    // Detect OS
    let os = 'Unknown';
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';

    // Map device type to our type (small-mobile -> mobile for simplicity)
    const deviceType = DeviceDetector.getDeviceType();
    const mappedType: 'mobile' | 'tablet' | 'desktop' | 'unknown' =
      deviceType === 'small-mobile' ? 'mobile' :
      deviceType === 'mobile' ? 'mobile' :
      deviceType === 'tablet' ? 'tablet' : 'desktop';

    return {
      type: mappedType,
      browser,
      os,
      isVR: false, // Will be updated when VR session starts
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
    };
  }

  /**
   * Start batch timer for periodic writes to Firestore
   */
  private startBatchTimer(): void {
    this.batchTimer = setInterval(() => {
      this.flushQueue();
    }, this.batchInterval);
  }

  /**
   * Start FPS monitoring (every 5 seconds)
   */
  private startFPSMonitoring(): void {
    // Track FPS every 5 seconds
    this.fpsMonitorInterval = setInterval(() => {
      const fps = this.calculateFPS();
      if (fps > 0) {
        this.trackPerformance(fps, this.currentStageIndex);
      }
    }, 5000);

    // Also set up a render loop counter to properly calculate FPS
    const countFrame = () => {
      this.frameCount++;
      requestAnimationFrame(countFrame);
    };
    requestAnimationFrame(countFrame);
  }

  /**
   * Calculate FPS
   */
  calculateFPS(): number {
    const now = performance.now();
    const delta = now - this.lastFrameTime;
    this.frameCount++;

    if (delta >= 1000) { // Every second
      const fps = Math.round((this.frameCount * 1000) / delta);
      this.frameCount = 0;
      this.lastFrameTime = now;
      return fps;
    }

    return 0; // Return 0 if not ready to calculate
  }

  /**
   * Queue an event for batch processing
   */
  private queueEvent(eventType: AnalyticsEventType, eventData: AnalyticsEventData): void {
    if (!this.enabled) return;

    this.eventQueue.push({
      eventType,
      eventData,
      timestamp: Date.now(),
    });

    // Also log to Firebase Analytics immediately (for standard web analytics)
    if (this.analytics) {
      logEvent(this.analytics, eventType, {
        session_id: this.sessionId,
        sequence_id: this.sequenceId,
      });
    }

    logger.debug(' Event queued:', eventType, eventData);
  }

  /**
   * Flush event queue to Firestore
   */
  private async flushQueue(): Promise<void> {
    if (!this.enabled || !this.firestore || this.eventQueue.length === 0) return;

    logger.debug(` Flushing ${this.eventQueue.length} events to Firestore...`);

    const eventsToWrite = [...this.eventQueue];
    this.eventQueue = [];

    try {
      const analyticsCollection = collection(this.firestore, 'analytics_events');

      // Write all events
      const writePromises = eventsToWrite.map(async (queuedEvent) => {
        const analyticsEvent: AnalyticsEvent = {
          sessionId: this.sessionId,
          sequenceId: this.sequenceId,
          userId: this.userId,
          timestamp: Timestamp.fromMillis(queuedEvent.timestamp),
          eventType: queuedEvent.eventType,
          eventData: queuedEvent.eventData,
          deviceInfo: this.deviceInfo,
        };

        await addDoc(analyticsCollection, analyticsEvent);
      });

      await Promise.all(writePromises);
      logger.debug(` ✅ Successfully wrote ${eventsToWrite.length} events`);
    } catch (error) {
      logger.error(' ❌ Failed to write events:', error);
      // Re-queue failed events
      this.eventQueue.push(...eventsToWrite);
    }
  }

  // ========== EVENT TRACKING METHODS ==========

  /**
   * Track session started
   */
  trackSessionStarted(): void {
    const eventData: SessionEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
    };

    this.queueEvent(AnalyticsEventType.SESSION_STARTED, eventData);
  }

  /**
   * Track session ended
   */
  trackSessionEnded(): void {
    const duration = Date.now() - this.sessionStartTime;

    const eventData: SessionEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      duration,
    };

    this.queueEvent(AnalyticsEventType.SESSION_ENDED, eventData);

    // Flush immediately on session end
    this.flushQueue();
  }

  /**
   * Track sequence loaded
   */
  trackSequenceLoaded(sequenceName: string, isPublic: boolean, hasPassword: boolean, organizationId: string | null): void {
    const eventData: SequenceEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      sequenceName,
      isPublic,
      hasPassword,
      organizationId,
    };

    this.queueEvent(AnalyticsEventType.SEQUENCE_LOADED, eventData);
  }

  /**
   * Track sequence view started
   */
  trackSequenceViewStarted(): void {
    const eventData: SequenceEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
    };

    this.queueEvent(AnalyticsEventType.SEQUENCE_VIEW_STARTED, eventData);
  }

  /**
   * Track sequence view completed
   */
  trackSequenceViewCompleted(): void {
    const eventData: SequenceEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
    };

    this.queueEvent(AnalyticsEventType.SEQUENCE_VIEW_COMPLETED, eventData);
  }

  /**
   * Track stage navigation
   */
  trackStageNavigation(fromStage: number, toStage: number, method: 'next' | 'previous' | 'jump' | 'selector', totalStages: number): void {
    const eventData: StageNavigationData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      fromStage,
      toStage,
      method,
      totalStages,
    };

    this.queueEvent(AnalyticsEventType.STAGE_NAVIGATION, eventData);
  }

  /**
   * Track stage view started
   */
  trackStageViewStarted(stageIndex: number, stageName: string | undefined, hasVideo: boolean, hasModels: boolean, hasPlanes: boolean, hasAudio: boolean): void {
    // Store current stage index for reference in other tracking methods
    this.currentStageIndex = stageIndex;
    this.currentStageStartTime = Date.now();

    const eventData: StageViewData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex,
      stageName,
      hasVideo,
      hasModels,
      hasPlanes,
      hasAudio,
    };

    this.queueEvent(AnalyticsEventType.STAGE_VIEW_STARTED, eventData);
  }

  /**
   * Track stage view ended
   */
  trackStageViewEnded(stageIndex: number, stageName: string | undefined): void {
    const duration = this.currentStageStartTime ? Date.now() - this.currentStageStartTime : 0;

    const eventData: StageViewData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex,
      stageName,
      duration,
    };

    this.queueEvent(AnalyticsEventType.STAGE_VIEW_ENDED, eventData);
  }

  /**
   * Track stage selector opened
   */
  trackStageSelectorOpened(mode: '2D' | 'VR', stageIndex: number): void {
    const eventData: StageSelectorData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      mode,
      stageIndex,
    };

    this.queueEvent(AnalyticsEventType.STAGE_SELECTOR_OPENED, eventData);
  }

  /**
   * Track stage selector closed
   */
  trackStageSelectorClosed(mode: '2D' | 'VR', stageIndex: number): void {
    const eventData: StageSelectorData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      mode,
      stageIndex,
    };

    this.queueEvent(AnalyticsEventType.STAGE_SELECTOR_CLOSED, eventData);
  }

  /**
   * Track VR session started
   */
  trackVRSessionStarted(): void {
    this.isInVR = true;
    this.deviceInfo.isVR = true;
    this.vrSessionStartTime = Date.now();

    const eventData: VRSessionData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
    };

    this.queueEvent(AnalyticsEventType.VR_SESSION_STARTED, eventData);
  }

  /**
   * Track VR session ended
   */
  trackVRSessionEnded(): void {
    const duration = this.vrSessionStartTime ? Date.now() - this.vrSessionStartTime : 0;
    this.isInVR = false;
    this.deviceInfo.isVR = false;

    const eventData: VRSessionData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      duration,
    };

    this.queueEvent(AnalyticsEventType.VR_SESSION_ENDED, eventData);
  }

  /**
   * Track VR state changed
   */
  trackVRStateChanged(state: 'entering' | 'in' | 'exiting' | 'not_in'): void {
    const eventData: VRSessionData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      state,
    };

    this.queueEvent(AnalyticsEventType.VR_STATE_CHANGED, eventData);
  }

  /**
   * Track VR controller connected
   */
  trackVRControllerConnected(handedness: 'left' | 'right' | 'unknown', controllerType?: string): void {
    const eventData: VRControllerData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      handedness,
      controllerType,
    };

    this.queueEvent(AnalyticsEventType.VR_CONTROLLER_CONNECTED, eventData);
  }

  /**
   * Track video play
   */
  trackVideoPlay(stageIndex: number, videoUrl?: string): void {
    const eventData: VideoEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex,
      videoUrl,
    };

    this.queueEvent(AnalyticsEventType.VIDEO_PLAY, eventData);
  }

  /**
   * Track video pause
   */
  trackVideoPause(stageIndex: number, currentTime?: number): void {
    const eventData: VideoEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex,
      currentTime,
    };

    this.queueEvent(AnalyticsEventType.VIDEO_PAUSE, eventData);
  }

  /**
   * Track video complete
   */
  trackVideoComplete(stageIndex: number, duration?: number): void {
    const eventData: VideoEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex,
      duration,
    };

    this.queueEvent(AnalyticsEventType.VIDEO_COMPLETE, eventData);
  }

  /**
   * Track video error
   */
  trackVideoError(stageIndex: number, errorMessage: string): void {
    const eventData: VideoEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex,
      errorMessage,
    };

    this.queueEvent(AnalyticsEventType.VIDEO_ERROR, eventData);
  }

  /**
   * Track FOV zoom
   */
  trackFOVZoom(newFOV: number, previousFOV: number, method: 'wheel' | 'pinch'): void {
    const eventData: FOVZoomData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      newFOV,
      previousFOV,
      method,
    };

    this.queueEvent(AnalyticsEventType.FOV_ZOOM_USED, eventData);
  }

  /**
   * Track controller button press
   */
  trackControllerButton(button: 'trigger-left' | 'trigger-right' | 'a' | 'b' | 'x' | 'y', action: string, stageIndex?: number): void {
    const eventData: ControllerButtonData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      button,
      action,
      stageIndex,
    };

    this.queueEvent(AnalyticsEventType.CONTROLLER_BUTTON_PRESSED, eventData);
  }

  /**
   * Track performance metrics
   */
  trackPerformance(fps: number, stageIndex?: number): void {
    const eventData: PerformanceEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      fps,
      stageIndex,
    };

    this.queueEvent(AnalyticsEventType.PERFORMANCE_METRIC, eventData);
  }

  /**
   * Track stage load time
   */
  trackStageLoadTime(stageIndex: number, loadTime: number): void {
    const eventData: PerformanceEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex,
      loadTime,
      resourceType: 'stage',
    };

    this.queueEvent(AnalyticsEventType.STAGE_LOAD_TIME, eventData);
  }

  /**
   * Track media load time
   */
  trackMediaLoadTime(resourceType: 'model' | 'video' | 'image', resourceUrl: string, loadTime: number, stageIndex?: number): void {
    const eventData: PerformanceEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex,
      loadTime,
      resourceType,
      resourceUrl,
    };

    this.queueEvent(AnalyticsEventType.MEDIA_LOAD_TIME, eventData);
  }

  /**
   * Track error
   */
  trackError(errorType: string, errorMessage: string, stack?: string, stageIndex?: number): void {
    const eventData: ErrorEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      errorType,
      errorMessage,
      stack,
      stageIndex,
    };

    this.queueEvent(AnalyticsEventType.ERROR_OCCURRED, eventData);
  }

  // ========== LEAD FORM TRACKING METHODS ==========

  /**
   * Track lead form viewed
   */
  trackLeadFormViewed(hotspotId: string, formType: string, fieldCount: number): void {
    const eventData: LeadFormEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex: this.currentStageIndex,
      hotspotId,
      formType,
      isVR: this.isInVR,
      fieldCount,
    };

    this.queueEvent(AnalyticsEventType.LEAD_FORM_VIEWED, eventData);
  }

  /**
   * Track lead form submitted
   */
  trackLeadFormSubmitted(hotspotId: string, formType: string, fieldCount: number): void {
    const eventData: LeadFormEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex: this.currentStageIndex,
      hotspotId,
      formType,
      isVR: this.isInVR,
      fieldCount,
      filledFields: fieldCount, // All fields filled on successful submission
    };

    this.queueEvent(AnalyticsEventType.LEAD_FORM_SUBMITTED, eventData);
  }

  /**
   * Track lead form abandoned (closed without submitting)
   */
  trackLeadFormAbandoned(hotspotId: string, formType: string, fieldCount: number, filledFields: number): void {
    const eventData: LeadFormEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex: this.currentStageIndex,
      hotspotId,
      formType,
      isVR: this.isInVR,
      fieldCount,
      filledFields,
    };

    this.queueEvent(AnalyticsEventType.LEAD_FORM_ABANDONED, eventData);
  }

  /**
   * Track lead form error (validation or submission error)
   */
  trackLeadFormError(hotspotId: string, formType: string, errorMessage: string): void {
    const eventData: LeadFormEventData = {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      sequenceId: this.sequenceId,
      userId: this.userId,
      stageIndex: this.currentStageIndex,
      hotspotId,
      formType,
      isVR: this.isInVR,
      errorMessage,
    };

    this.queueEvent(AnalyticsEventType.LEAD_FORM_ERROR, eventData);
  }

  /**
   * Get current stage index (for tracking purposes)
   */
  getCurrentStageIndex(): number {
    return this.currentStageIndex;
  }

  /**
   * Get session ID (for lead form submissions and tracking)
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Check if currently in VR
   */
  getIsInVR(): boolean {
    return this.isInVR;
  }

  /**
   * Opt out of analytics
   */
  static optOut(): void {
    localStorage.setItem('analytics_optout', 'true');
    logger.debug(' User opted out of analytics');
  }

  /**
   * Opt in to analytics
   */
  static optIn(): void {
    localStorage.removeItem('analytics_optout');
    logger.debug(' User opted in to analytics');
  }

  /**
   * Check if opted out
   */
  static isOptedOut(): boolean {
    return localStorage.getItem('analytics_optout') === 'true';
  }

  /**
   * Dispose and cleanup
   */
  dispose(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.fpsMonitorInterval) {
      clearInterval(this.fpsMonitorInterval);
      this.fpsMonitorInterval = null;
    }

    // Final flush
    this.flushQueue();
  }
}
